import React, { useCallback, useMemo, useRef, useState, ReactNode } from 'react';
import { css } from '@emotion/react';
import {
  InvoiceFilterStatus,
  InvoiceModel,
  InvoiceSearchParams,
  InvoiceStatus,
  useCancelInvoiceMutation,
} from '@frontend/api-invoices';
import {
  PaymentMethod,
  PaymentMethodStatus,
  PaymentPlanQueries,
  setDefaultPaymentMethod,
} from '@frontend/api-payment-plans';
import { PersonsV3, PersonTypes } from '@frontend/api-person';
import { PaymentsProfilePromotionWrapper } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import {
  CareCreditPersonStatusItem,
  useCareCreditLocationProviderStatus,
  useCareCreditPatientData,
} from '@frontend/payments-care-credit';
import {
  CollectPaymentMulti,
  CollectPaymentMultiModal,
  CollectPaymentMultiPortal,
  PAYMENT_MULTI_STEPS,
  useCollectPaymentMulti,
  usePaymentsModalButton,
  useSyncCollectPaymentMultiToModal,
} from '@frontend/payments-collection-flow';
import { paymentsSentry, useMerchants } from '@frontend/payments-hooks';
import { useInvoiceShallowStore, useUserInvoicesInRange } from '@frontend/payments-invoice-controller';
import { GenericPaymentMethodsModal } from '@frontend/payments-platform';
import { REQUEST_ORIGINS } from '@frontend/person-invoice-create';
import { usePersonActiveInvoiceList } from '@frontend/person-invoice-list';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import {
  Button,
  ConfirmationModal,
  Heading,
  ModalControlTriggerProps,
  SkeletonLoader,
  Text,
  useModalControl,
} from '@frontend/design-system';
import { ActionItem } from './payments-panel/action-item';
import { InvoiceCardActions } from './payments-panel/invoice-card-actions';
import { EmptyInvoiceChart, InvoiceSummaryChart } from './payments-panel/invoice-summary-chart';
import { OutstandingInvoiceCards, SortOptions } from './payments-panel/outstanding-invoice-cards';

export type PaymentsChartData = {
  key: string;
  label: string;
  value: number;
}[];

export type PaymentsPanelProps = {
  locationId: string | undefined;
  trackingId: string;
  className?: string;
  person?: PersonTypes.Person;
  isActive?: boolean;
};

const styles = {
  parentWrapper: css`
    padding: ${theme.spacing(3)};
    background: ${theme.colors.neutral5};
  `,
  parent: css`
    display: grid;
    row-gap: ${theme.spacing(2)};
    grid-template-columns: 1fr;
    grid-auto-rows: min-content;
  `,
};

const PAYMENT_SUCCESS_TIMEOUT = 2000;

type CollectModalUiProps = {
  paymentTriggerProps: ModalControlTriggerProps;
  PaymentModal: ReactNode;
  invoicesInRange: InvoiceModel[];
  onChangeInvoiceId: (invoiceId?: string) => void;
  refetchInvoicesCallback: () => void;
  onGoToPrequalifyStep: () => void;
  goToCreateStep: () => void;
  goToPaymentMethodsStep: () => void;
  goToShareInMessageStep: () => void;
};

export function PaymentsPanel({ person, locationId, trackingId, ...rest }: PaymentsPanelProps) {
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>();

  // invoices for last 90 days
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 90);
  startDate.setHours(0, 0, 0, 0);

  const dateRange = useMemo(() => {
    return {
      start: startDate.toISOString(),
      end: new Date().toISOString(),
    };
  }, [startDate.toISOString()]);

  const { invoices: invoicesInRange, refetch: refetchInvoicesInRange } = useUserInvoicesInRange({
    personId: person?.PersonID || '',
    dateRange,
  });

  const { refetchInvoices } = usePersonActiveInvoiceList(person?.PersonID);

  const refetchInvoicesCallback = useCallback(() => {
    setTimeout(() => {
      refetchInvoices();
      refetchInvoicesInRange();
    }, PAYMENT_SUCCESS_TIMEOUT);
  }, [refetchInvoices, refetchInvoicesInRange]);

  const { triggerProps: paymentTriggerProps, modalProps, closeModal: closePaymentModal } = useModalControl();

  const collectProps = useCollectPaymentMulti(
    {
      personId: person?.PersonID,
      locationId,
      invoiceId: selectedInvoiceId,
      trackingId: trackingId,
      onPaymentSuccess: refetchInvoicesCallback,
      onInvoiceCreated: refetchInvoicesCallback,
    },
    {
      trackingId: trackingId,
      onCancel: () => {
        closePaymentModal();
      },
      onComplete: () => {
        closePaymentModal();
      },
    }
  );

  const { disableCloseOnEscape, disableCloseOnOverlayClick } = useSyncCollectPaymentMultiToModal({
    activeStep: collectProps.multiStep.activeStep,
    setShowCancelAction: collectProps.multiStep.setShowCancelAction,
  });

  const { beforeUnmountCallback } = collectProps;
  const onCloseModalDirectly = useCallback(() => {
    closePaymentModal();
    beforeUnmountCallback();
  }, [closePaymentModal, beforeUnmountCallback]);

  const data = {
    paymentTriggerProps,
    PaymentModal: (
      <>
        <CollectPaymentMultiModal
          {...modalProps}
          onClose={onCloseModalDirectly}
          minWidth={600}
          disableCloseOnEscape={disableCloseOnEscape}
          disableCloseOnOverlayClick={disableCloseOnOverlayClick}
        >
          <CollectPaymentMulti {...collectProps} origin={REQUEST_ORIGINS.CONTACT_PANEL_PAYMENTS_TAB} />
        </CollectPaymentMultiModal>
        <CollectPaymentMultiPortal multiControl={collectProps} locationId={locationId} />
      </>
    ),
    goToCreateStep: () => {
      collectProps.setFlowAndStep('create', PAYMENT_MULTI_STEPS.create);
    },
    goToPaymentMethodsStep: () => {
      collectProps.setFlowAndStep('pay', PAYMENT_MULTI_STEPS.paymentMethods);
    },
    goToShareInMessageStep: () => {
      collectProps.setFlowAndStep('pay', PAYMENT_MULTI_STEPS.shareInMessage);
    },
    onGoToPrequalifyStep: () => {
      collectProps.setFlowAndStep('pay', PAYMENT_MULTI_STEPS.careCreditEnroll);
    },
  };

  return (
    <PaymentsPanelUI
      locationId={locationId}
      trackingId={trackingId}
      invoicesInRange={invoicesInRange}
      refetchInvoicesCallback={refetchInvoicesCallback}
      person={person}
      onChangeInvoiceId={setSelectedInvoiceId}
      {...data}
      {...rest}
    />
  );
}

export function PaymentsPanelUI({
  locationId,
  person,
  isActive = true,
  trackingId,
  paymentTriggerProps,
  PaymentModal,
  invoicesInRange,
  onChangeInvoiceId,
  refetchInvoicesCallback,
  goToCreateStep,
  goToPaymentMethodsStep,
  goToShareInMessageStep,
  onGoToPrequalifyStep,
  ...rest
}: PaymentsPanelProps & CollectModalUiProps) {
  const { merchant, hasPayments } = useMerchants(locationId)['firstLocation'];
  const {
    data: paymentMethods,
    isLoading: isLoadingPaymentMethods,
    refetch: refetchPaymentMethods,
  } = PaymentPlanQueries.useGetPaymentMethodsByPerson(
    {
      locationId: locationId ?? '',
      patientId: person?.PersonID || '',
    },
    {
      enabled: hasPayments,
    }
  );
  const { disabledButtonProps } = usePaymentsModalButton({ personId: person?.PersonID });

  const { modalProps: editBillingModalProps, triggerProps: editBillingTriggerProps } = useModalControl();
  const { modalProps: deleteRequestModalProps, triggerProps: deleteRequestTriggerProps } = useModalControl({
    onClose: () => {
      selectedInvoiceToDelete.current = null;
    },
  });

  const { setFilter, setFilterDisplayDates } = useInvoiceShallowStore('setFilter', 'setFilterDisplayDates');
  const navigate = useNavigate();

  const { t } = useTranslation('payments');

  const { isProviderConnected: isCareCreditEnabled, merchantNumber } = useCareCreditLocationProviderStatus({
    locationId,
  });

  const v3Person = useMemo(() => {
    if (!person) {
      return undefined;
    }

    return PersonsV3.PersonHelpers.convertPersonToPersonV3(person);
  }, [person]);

  const { careCreditData, isLoading: isLoadingCareCreditData } = useCareCreditPatientData(
    { person: v3Person, merchantNumber, locationId },
    { enabled: isCareCreditEnabled && !!person?.PersonID }
  );

  const [selectedOrder, setSelectedOrder] = React.useState<SortOptions>('newest');

  const orderParam = useMemo(() => {
    const map: Record<SortOptions, InvoiceSearchParams['order']> = {
      newest: '-billedAt',
      oldest: 'billedAt',
      highest: '-billedAmount',
      lowest: 'billedAmount',
    };

    return map[selectedOrder];
  }, [selectedOrder]);

  const { invoices: activeInvoices } = usePersonActiveInvoiceList(person?.PersonID);

  const { cancelInvoice } = useCancelInvoiceMutation({
    locationId: locationId ?? '',
    onSuccess: () => {
      refetchInvoicesCallback();
    },
  });
  const selectedInvoiceToDelete = useRef<InvoiceModel | null>(null);

  const sortedActiveInvoices = useMemo(() => {
    if (!activeInvoices) {
      return [];
    }
    /**
     * hard coded sort order. No need to re-sort.
     */
    if (orderParam === '-billedAt') {
      return activeInvoices;
    }

    return activeInvoices?.sort((a, b) => {
      if (orderParam === 'billedAt') {
        return new Date(a.billedAt).getTime() - new Date(b.billedAt).getTime();
      }

      if (orderParam === '-billedAmount') {
        return b.billedAmount - a.billedAmount;
      }

      return a.billedAmount - b.billedAmount;
    });
  }, [activeInvoices, orderParam]);

  const balances = useMemo(() => {
    return invoicesInRange.reduce(
      (acc, invoice) => {
        if (invoice.status === InvoiceStatus.Paid || invoice.status === InvoiceStatus.PartiallyPaid) {
          return { ...acc, paid: acc.paid + invoice.billedAmount };
        }
        return { ...acc, outstanding: acc.outstanding + invoice.billedAmount };
      },
      { paid: 0, outstanding: 0 }
    );
  }, [invoicesInRange]);

  const chartData = useMemo(() => {
    return [
      { key: 'paid', label: t('Paid'), value: balances.paid },
      { key: 'outstanding', label: t('Still Owed'), value: balances.outstanding },
    ] satisfies PaymentsChartData;
  }, [t, balances]);

  const defaultPaymentMethod = useMemo(() => {
    const filteredPaymentMethods = paymentMethods?.filter(
      (pm) =>
        !(
          pm.status &&
          [
            PaymentMethodStatus.PAYMENT_METHOD_STATUS_VERIFICATION_PENDING,
            PaymentMethodStatus.PAYMENT_METHOD_STATUS_EXPIRED,
          ].includes(pm.status)
        )
    );

    const foundValidDefault = filteredPaymentMethods?.find(
      (pm) => (pm.is_default && pm.type === 'card') || (pm.is_default && pm.type === 'us_bank_account')
    );
    return foundValidDefault || filteredPaymentMethods?.find((pm) => pm.type === 'card');
  }, [paymentMethods]);

  const isLoadingAll = useMemo(() => {
    return isLoadingPaymentMethods || !person;
  }, [person, isLoadingPaymentMethods]);

  const firstName = useMemo(() => {
    return person?.FirstName || '';
  }, [person]);

  const handleSetBillingMethod = async (paymentMethod: PaymentMethod) => {
    try {
      await setDefaultPaymentMethod(paymentMethod.id, locationId ?? '');
      editBillingModalProps.onClose();
      refetchPaymentMethods();
    } catch (error) {
      paymentsSentry.error(
        'Failed to set default billing method',
        'PaymentsPanel',
        error instanceof Error ? error.message : 'Unknown error setting default billing method'
      );
    }
  };

  const getPaymentMethodText = (pm: PaymentMethod) => {
    if (pm.type === 'card') {
      return `${pm.card.brand} ${pm.card.last4}`;
    } else if (pm.type === 'us_bank_account') {
      return `${pm.bankAccount.accountType} ${pm.bankAccount.last4}`;
    }

    return '';
  };

  const onClickCreateInvoice = useCallback(() => {
    onChangeInvoiceId(undefined);
    goToCreateStep();
    paymentTriggerProps.onClick();
  }, [paymentTriggerProps]);

  const onClickCollectPaymentFromInvoice = useCallback(
    (invoice: InvoiceModel) => {
      onChangeInvoiceId(invoice.id);
      goToPaymentMethodsStep();
      paymentTriggerProps.onClick();
    },
    [paymentTriggerProps.onClick]
  );

  const onClickRemindFromInvoice = useCallback(
    (invoice: InvoiceModel) => {
      onChangeInvoiceId(invoice.id);
      goToShareInMessageStep();
      paymentTriggerProps.onClick();
    },
    [paymentTriggerProps.onClick]
  );

  const onClickPrequalify = useCallback(() => {
    onGoToPrequalifyStep();
    paymentTriggerProps.onClick();
  }, [paymentTriggerProps.onClick]);

  const onClickViewInvoice = useCallback(
    (invoice: InvoiceModel) => {
      navigate({ to: '/payments/invoices/$id', params: { id: invoice.id } });
    },
    [navigate]
  );

  const onClickDeleteInvoice = useCallback(
    (invoice: InvoiceModel) => {
      deleteRequestTriggerProps.onClick();
      selectedInvoiceToDelete.current = invoice;
    },
    [deleteRequestTriggerProps.onClick]
  );

  const handleConfirmDelete = useCallback(() => {
    if (selectedInvoiceToDelete.current) {
      cancelInvoice(selectedInvoiceToDelete.current);
      selectedInvoiceToDelete.current = null;
    }
  }, []);

  const onClickViewHistory = () => {
    setFilter({
      status: [],
      person: `id:${person?.PersonID}`,
    });
    setFilterDisplayDates({ start: undefined, end: undefined });
    navigate({ to: '/payments/invoices', search: { status: 'allInvoices' } });
  };

  const onClickViewPaidHistory = () => {
    setFilter({
      status: [InvoiceFilterStatus.paid, InvoiceFilterStatus.partiallyPaid],
      person: `id:${person?.PersonID}`,
    });
    setFilterDisplayDates({ start: undefined, end: undefined });
    navigate({ to: '/payments/invoices', search: { status: 'paid' } });
  };

  return (
    <>
      <SkeletonLoader isLoading={isLoadingAll}>
        <div {...rest} css={styles.parentWrapper}>
          <PaymentsProfilePromotionWrapper locationId={locationId ?? ''}>
            <div css={styles.parent}>
              {isActive && (
                <>
                  {/* Only show chart when tab is active, to avoid responsiveness warnings */}
                  <div>
                    {invoicesInRange.length > 0 ? (
                      <>
                        <Heading level={3}>
                          <Text as='span' size='inherit' weight='regular'>
                            {t('Activity:')}
                          </Text>
                          <Text as='span' size='inherit' weight='bold' css={{ paddingLeft: theme.spacing(1) }}>
                            {t('Last {{days}} days', { days: 90 })}
                          </Text>
                        </Heading>
                        <InvoiceSummaryChart data={chartData} />
                      </>
                    ) : (
                      <EmptyInvoiceChart trackingId={trackingId} onClickOverlayBtn={onClickCreateInvoice} />
                    )}
                  </div>
                </>
              )}
              {sortedActiveInvoices.length > 0 && (
                <OutstandingInvoiceCards
                  trackingId={trackingId}
                  altAction={
                    <Button
                      variant='tertiary'
                      size='large'
                      iconName='plus'
                      trackingId={`${trackingId}--request-payment-btn`}
                      onClick={onClickCreateInvoice}
                      {...disabledButtonProps}
                    >
                      {t('Payment Request')}
                    </Button>
                  }
                  invoices={sortedActiveInvoices}
                  onSelectOrder={setSelectedOrder}
                  selectedOrder={selectedOrder}
                  cardActions={({ invoice }) => (
                    <InvoiceCardActions
                      onClickRemind={onClickRemindFromInvoice}
                      onClickCollect={onClickCollectPaymentFromInvoice}
                      onClickView={onClickViewInvoice}
                      onClickDelete={onClickDeleteInvoice}
                      trackingId={trackingId}
                      invoice={invoice}
                    />
                  )}
                />
              )}
              {defaultPaymentMethod ? (
                <ActionItem
                  prefix={<Icon name='credit-card' size={16} />}
                  suffixText={getPaymentMethodText(defaultPaymentMethod)}
                  trackingId={`${trackingId}--edit-billing`}
                  {...editBillingTriggerProps}
                >
                  {t('Card On File')}
                </ActionItem>
              ) : (
                <ActionItem
                  trackingId={`${trackingId}--add-billing`}
                  prefix={<Icon name='credit-card' size={16} />}
                  suffixText='-'
                  {...editBillingTriggerProps}
                >
                  {t('Add Card On File')}
                </ActionItem>
              )}
              <ActionItem
                onClick={onClickViewHistory}
                prefix={<Icon name='history' size={16} />}
                suffixIcon={<Icon name='caret-right' size={16} />}
                trackingId={`${trackingId}--view-payment-history`}
              >
                {firstName ? t(`{{firstName}}'s Payment Request History`, { firstName }) : t('Payment Request History')}
              </ActionItem>

              <ActionItem
                onClick={onClickViewPaidHistory}
                prefix={<Icon name='history' size={16} />}
                suffixIcon={<Icon name='caret-right' size={16} />}
                trackingId={`${trackingId}--view-paid-history`}
              >
                {firstName ? t(`{{firstName}}'s Paid History`, { firstName }) : t('Paid History')}
              </ActionItem>

              {isCareCreditEnabled && (
                <CareCreditPersonStatusItem
                  careCreditData={careCreditData}
                  isLoading={isLoadingCareCreditData}
                  onClickPrequalify={onClickPrequalify}
                />
              )}
            </div>
          </PaymentsProfilePromotionWrapper>
        </div>
      </SkeletonLoader>
      <GenericPaymentMethodsModal
        modalProps={editBillingModalProps}
        merchant={merchant}
        locationId={locationId || ''}
        person={person}
        onSelectPaymentMethod={handleSetBillingMethod}
      />
      <ConfirmationModal
        {...deleteRequestModalProps}
        destructive
        title={t('Delete Payment Request')}
        message={t('This cannot be undone. Are you sure you want to delete this request?')}
        onConfirm={handleConfirmDelete}
      />
      {PaymentModal}
    </>
  );
}
